/**
 * Lightweight wellness-timer preference store backed by localStorage,
 * with a custom event so multiple components stay in sync.
 *
 * Keys:
 *   wellness:enabled        "1" | "0"   (default: "1")
 *   wellness:thresholdMin   integer string (default: "10")
 */
import { useEffect, useState } from "react";

const KEY_ENABLED = "wellness:enabled";
const KEY_THRESHOLD = "wellness:thresholdMin";
const EVT = "wellness:changed";

export const WELLNESS_OPTIONS = [
  { value: 5, label: "5 minutes" },
  { value: 10, label: "10 minutes" },
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 60, label: "1 hour" },
];

function readEnabled() {
  try {
    const v = window.localStorage.getItem(KEY_ENABLED);
    if (v === null) return true;
    return v === "1";
  } catch (e) { return true; }
}

function readThreshold() {
  try {
    const v = parseInt(window.localStorage.getItem(KEY_THRESHOLD) || "10", 10);
    if (!Number.isFinite(v) || v <= 0) return 10;
    return v;
  } catch (e) { return 10; }
}

export function getWellnessSettings() {
  return { enabled: readEnabled(), thresholdMin: readThreshold() };
}

export function setWellnessSettings({ enabled, thresholdMin }) {
  try {
    if (typeof enabled === "boolean") {
      window.localStorage.setItem(KEY_ENABLED, enabled ? "1" : "0");
    }
    if (typeof thresholdMin === "number" && thresholdMin > 0) {
      window.localStorage.setItem(KEY_THRESHOLD, String(thresholdMin));
    }
    window.dispatchEvent(new CustomEvent(EVT));
  } catch (e) { /* ignore */ }
}

/**
 * Hook — subscribe to wellness settings changes across the app.
 */
export function useWellnessSettings() {
  const [state, setState] = useState(() => getWellnessSettings());
  useEffect(() => {
    const sync = () => setState(getWellnessSettings());
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return state;
}
